import type { SubIssue, SubIssues, SubscriptionData } from "./types";

export function isTreatByInfo(info: { tags?: string; productTitle?: string; product_type?: string } | null): boolean {
  if (!info) return false;
  const tags = String(info.tags || "").toLowerCase();
  const title = String(info.productTitle || "").toLowerCase();
  const productType = String(info.product_type || "").toLowerCase();

  if (tags.includes("treat")) return true;
  if (productType === "treat" || productType === "treats") return true;

  return (
    title.includes("stick") ||
    title.includes("strip") ||
    title.includes("pizzle") ||
    title.includes("biscuit") ||
    title.includes("snack") ||
    title.includes("chew") ||
    title.includes("dental")
  );
}

export function getVariantInfoFromProducts(variantId: string, products: any[]): {
  productTitle: string;
  variantTitle: string;
  tags: string;
  product_type: string;
  variantSize: number | null;
} | null {
  for (const product of products) {
    const variant = product.variants?.find((v: any) => String(v.id) === variantId);
    if (variant) {
      const sizeMatch = String(variant.title || "").match(/(\d+)/);
      return {
        productTitle: product.title || "Unknown",
        variantTitle: variant.title || "",
        tags: product.tags || "",
        product_type: product.product_type || "",
        variantSize: sizeMatch ? parseInt(sizeMatch[1], 10) : null,
      };
    }
  }
  return null;
}

export function checkSubIssues(sub: SubscriptionData, products: any[]): SubIssues {
  const issues: SubIssue[] = [];

  const mealsJsonStr = sub.properties?.find(p => p.name === "_paws_meals_json")?.value;
  const packSizeStr = sub.properties?.find(p => p.name === "_paws_pack_size_grams")?.value;
  const packSize = packSizeStr ? parseInt(packSizeStr, 10) : null;

  if (!mealsJsonStr) {
    return { hasIssues: false, issueCount: 0, issues: [] };
  }

  let meals: Array<{ variantId: string; count: number }> = [];
  try {
    meals = JSON.parse(mealsJsonStr);
  } catch {
    return { hasIssues: false, issueCount: 0, issues: [] };
  }

  if (!Array.isArray(meals) || meals.length === 0) {
    return { hasIssues: false, issueCount: 0, issues: [] };
  }

  const unknownVariants: Array<{ variantId: string; count: number; productTitle: string }> = [];
  const treatsInMeals: Array<{ variantId: string; count: number; productTitle: string }> = [];
  const mismatchedSizes: Array<{ variantId: string; count: number; productTitle: string; actualSize: number }> = [];

  for (const meal of meals) {
    if (!meal.variantId) continue;

    const info = getVariantInfoFromProducts(String(meal.variantId), products);

    if (!info) {
      unknownVariants.push({
        variantId: String(meal.variantId),
        count: meal.count || 0,
        productTitle: `Unknown (ID: ${meal.variantId})`,
      });
      continue;
    }

    if (isTreatByInfo(info)) {
      treatsInMeals.push({
        variantId: String(meal.variantId),
        count: meal.count || 0,
        productTitle: info.productTitle,
      });
      continue;
    }

    if (packSize && info.variantSize && info.variantSize !== packSize) {
      mismatchedSizes.push({
        variantId: String(meal.variantId),
        count: meal.count || 0,
        productTitle: info.productTitle,
        actualSize: info.variantSize,
      });
    }
  }

  if (unknownVariants.length > 0) {
    issues.push({
      type: "unknown",
      message: `${unknownVariants.length} meal(s) have variant IDs not found in products`,
      severity: "warning",
      details: unknownVariants.map(v => `${v.productTitle} (x${v.count})`),
      affectedMeals: unknownVariants,
    });
  }

  if (treatsInMeals.length > 0) {
    issues.push({
      type: "treats_in_meals",
      message: `${treatsInMeals.length} treat(s) incorrectly stored in meals JSON`,
      severity: "error",
      details: treatsInMeals.map(v => `${v.productTitle} (x${v.count})`),
      affectedMeals: treatsInMeals,
    });
  }

  if (mismatchedSizes.length > 0) {
    const actualSizes = [...new Set(mismatchedSizes.map(m => m.actualSize))];
    issues.push({
      type: "mismatch",
      message: `Pack size mismatch (stored ${packSize}g, variants are ${actualSizes.join("/")}g)`,
      severity: "warning",
      details: mismatchedSizes.map(v => `${v.productTitle} ${v.actualSize}g (x${v.count})`),
      affectedMeals: mismatchedSizes,
    });
  }

  return {
    hasIssues: issues.length > 0,
    issueCount: issues.length,
    issues,
  };
}

export function findHiddenPriceProduct(products: any[], planDays: number): any | null {
  return products.find((p) => {
    const handle = String(p.handle || "").toLowerCase();
    const title = String(p.title || "").toLowerCase();
    return (
      (handle.includes("hidden") && handle.includes(`${planDays}`)) ||
      (title.includes("hidden") && title.includes(`${planDays} day`))
    );
  }) || null;
}

export function getPackPriceFromHidden(hiddenProduct: any | null, packSize: number): number {
  if (!hiddenProduct?.variants) return 0;
  const variant = hiddenProduct.variants.find((v: any) => {
    const sizeFromTitle = parseInt(String(v.title || ""), 10);
    return sizeFromTitle === packSize;
  });
  return variant ? parseFloat(String(variant.price || "0")) : 0;
}

export function findPlanProduct(products: any[], planDays: number): any | null {
  return products.find((p) => {
    const title = String(p.title || "").toLowerCase();
    const handle = String(p.handle || "").toLowerCase();
    const isHidden = handle.includes("hidden") || title.includes("hidden");
    if (isHidden) return false;
    return (
      title.includes(`${planDays} day plan`) ||
      title.includes(`${planDays}-day plan`) ||
      title.includes(`${planDays}day plan`)
    );
  }) || null;
}

export function getBundleVariantId(products: any[], planDays: number, packSize: number): string | null {
  const planProduct = findPlanProduct(products, planDays);
  if (!planProduct?.variants) return null;

  const variant = planProduct.variants.find((v: any) => {
    const sizeMatch = String(v.title || "").match(/(\d+)/);
    return sizeMatch && parseInt(sizeMatch[1], 10) === packSize;
  });

  return variant ? String(variant.id) : null;
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "Not set";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function addDays(dateStr: string, days: number): string | null {
  try {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  } catch {
    return null;
  }
}

/** @deprecated Validation is now handled server-side by Qivora. Always returns true. */
export function isValidDeliveryDay(_dateStr: string): boolean {
  return true;
}

export function formatPrice(amount: number): string {
  return `£${amount.toFixed(2)}`;
}

export function getProp(sub: { properties?: Array<{ name: string; value: string }> }, key: string): string | null {
  return sub.properties?.find(p => p.name === key)?.value ?? null;
}

export function safeJson(value: string | null, fallback: any = null) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export function remapMealsToPackSize(
  currentMeals: Array<{ variantId: string; count: number }>,
  targetPackSize: number,
  products: any[]
): Array<{ variantId: string; count: number; productTitle: string }> {
  const remappedMeals: Array<{ variantId: string; count: number; productTitle: string }> = [];

  for (const meal of currentMeals) {
    if (!meal.variantId || !meal.count) continue;

    const currentInfo = getVariantInfoFromProducts(String(meal.variantId), products);
    if (!currentInfo) {
      return [];
    }

    let found = false;
    for (const product of products) {
      if (product.title !== currentInfo.productTitle) continue;

      const targetVariant = product.variants?.find((v: any) => {
        const sizeMatch = String(v.title || "").match(/(\d+)/);
        return sizeMatch && parseInt(sizeMatch[1], 10) === targetPackSize;
      });

      if (targetVariant) {
        remappedMeals.push({
          variantId: String(targetVariant.id),
          count: meal.count,
          productTitle: currentInfo.productTitle,
        });
        found = true;
      }
      break;
    }

    if (!found) {
      remappedMeals.push({
        variantId: String(meal.variantId),
        count: meal.count,
        productTitle: currentInfo.productTitle,
      });
    }
  }

  return remappedMeals;
}
